import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel,
  LinearProgress, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Cancel, CheckCircle, Email, MarkEmailReadRounded, Payment, PersonAdd, Refresh, Schedule, Search, Sms, Visibility, Warning, WhatsApp,
} from '@mui/icons-material';
import { collection, onSnapshot } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../config/firebase';
import {
  alpha, fieldSx, PanelTitle, primaryButtonSx, quietButtonSx, StatusChip, tableContainerSx,
  WorkspaceHeader, WorkspacePage, WorkspacePanel, WorkspaceStatCard, workspacePalette,
} from '../common/AdminWorkspace';
import BrandedPageLoader from '../common/BrandedPageLoader';
import {
  asDate, expireRegistration, extendRegistration, getRegistrationReminderHistory, humanRegistrationStage,
  isIncompleteRegistration, reactivateRegistration, registrationFriendlyError, registrationProgress,
  resetRegistrationReminders, sendRegistrationReminder, resolveRegistrationResumeUrl,
} from '../../services/api/registrationAdminService';

const fmt = (value) => {
  const date = asDate(value);
  return date ? date.toLocaleString() : '—';
};

const verificationLabel = (u) => {
  const method = String(u.verificationMethod || '').toLowerCase();
  if (method === 'firebase_auth') return u.emailVerified === true ? 'Verified' : 'Unverified';
  return u.emailVerified === true || u.isVerified === true || u.verificationOverride === true ? 'Verified' : 'Unverified';
};
const toneForStage = (stage) => {
  const key = String(stage || '').toLowerCase();
  if (key.includes('payment')) return workspacePalette.amber;
  if (key.includes('verification')) return workspacePalette.purple;
  if (key.includes('expired')) return workspacePalette.danger;
  if (key.includes('approval')) return workspacePalette.teal;
  return workspacePalette.blue;
};

export default function IncompleteRegistrations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snap) => {
      const next = snap.docs.map((row) => ({ id: row.id, ...row.data() }))
        .filter(isIncompleteRegistration)
        .sort((a, b) => (asDate(b.updatedAt || b.createdAt)?.getTime() || 0) - (asDate(a.updatedAt || a.createdAt)?.getTime() || 0));
      setRows(next);
      setLoading(false);
    }, (error) => {
      console.error('Incomplete registrations listener:', error);
      toast.error('Unable to load incomplete registrations.');
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const stages = useMemo(() => Array.from(new Set(rows.map(humanRegistrationStage))).sort(), [rows]);
  const filtered = useMemo(() => rows.filter((row) => {
    const needle = search.trim().toLowerCase();
    const hay = `${row.displayName || ''} ${row.firstName || ''} ${row.lastName || ''} ${row.email || ''} ${row.phone || row.phoneNumber || ''} ${row.companyName || ''}`.toLowerCase();
    if (needle && !hay.includes(needle)) return false;
    if (stageFilter !== 'all' && humanRegistrationStage(row) !== stageFilter) return false;
    return true;
  }), [rows, search, stageFilter]);

  const stats = useMemo(() => ({
    total: rows.length,
    verification: rows.filter((row) => humanRegistrationStage(row).toLowerCase().includes('verification')).length,
    payment: rows.filter((row) => humanRegistrationStage(row).toLowerCase().includes('payment')).length,
    expiring: rows.filter((row) => {
      const expires = asDate(row.registrationExpiresAt);
      return expires && expires.getTime() > Date.now() && expires.getTime() - Date.now() < 3 * 86400000;
    }).length,
  }), [rows]);

  const openDetail = async (row) => {
    setDetail(row);
    setHistory([]);
    const entries = await getRegistrationReminderHistory(row.id);
    setHistory(entries);
  };

  const run = async (fn, success) => {
    setActionBusy(true);
    try { await fn(); toast.success(success); }
    catch (error) { console.error(success, error); toast.error(registrationFriendlyError(error)); }
    finally { setActionBusy(false); }
  };

  const sendReminder = (row, channels = ['in_app','email','whatsapp']) => run(
    () => sendRegistrationReminder(row, channels),
    'Registration reminder sent.'
  );

  const sendBulk = async () => {
    const targets = rows.filter((row) => selected.includes(row.id));
    if (!targets.length) return;
    setActionBusy(true);
    let sent = 0;
    try {
      for (const row of targets) {
        try { await sendRegistrationReminder(row, ['in_app','email','whatsapp']); sent += 1; }
        catch (error) { console.error('Bulk reminder failed:', row.id, error); }
      }
      toast.success(`${sent} reminder${sent === 1 ? '' : 's'} sent.`);
      setSelected([]);
    } finally { setActionBusy(false); }
  };

  const copyResume = async (row) => {
    const url = await resolveRegistrationResumeUrl(row);
    if (!url) { toast.error('No saved resume link is available for this registration.'); return; }
    try { await navigator.clipboard.writeText(url); toast.success('Resume link copied.'); }
    catch (error) { console.error('Clipboard error:', error); toast.error('Unable to copy the resume link.'); }
  };

  if (loading) return <WorkspacePage><BrandedPageLoader label="Loading incomplete registrations…" skeleton /></WorkspacePage>;

  return (
    <WorkspacePage>
      <WorkspaceHeader eyebrow="Registration Operations" title="Incomplete Registrations" subtitle="Accounts created on TELCOFAIR that have not finished registration. Resume, remind, expire or reactivate without changing the Company Portal registration flow." icon={<PersonAdd />} accent={workspacePalette.purple} action={<Button startIcon={<Refresh />} onClick={() => toast.success('Registration queue is already updating live.')} sx={quietButtonSx}>Live refresh</Button>} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', xl: 'repeat(4,1fr)' }, gap: 1.15, mb: 1.25 }}>
        <WorkspaceStatCard label="Incomplete" value={stats.total} hint="Accounts requiring action" icon={<PersonAdd />} accent={workspacePalette.purple} />
        <WorkspaceStatCard label="Verification" value={stats.verification} hint="Awaiting verification" icon={<MarkEmailReadRounded />} accent={workspacePalette.blue} />
        <WorkspaceStatCard label="Payment pending" value={stats.payment} hint="Registration or extra-seat" icon={<Payment />} accent={workspacePalette.amber} />
        <WorkspaceStatCard label="Expiring soon" value={stats.expiring} hint="Within the next 3 days" icon={<Schedule />} accent={workspacePalette.danger} />
      </Box>

      <WorkspacePanel accent={workspacePalette.purple}>
        <PanelTitle title="Registration Queue" subtitle={`${filtered.length} visible · ${selected.length} selected`} icon={<PersonAdd />} accent={workspacePalette.purple} action={selected.length > 0 ? <Button disabled={actionBusy} startIcon={<Email />} onClick={sendBulk} sx={primaryButtonSx}>Send bulk reminder</Button> : null} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(240px,1fr) 220px' }, gap: .9, mb: 1.1 }}>
          <TextField value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone or company" InputProps={{ startAdornment: <Search sx={{ mr: .7, color: workspacePalette.inkFaint, fontSize: 18 }} /> }} sx={fieldSx} />
          <FormControl size="small" sx={fieldSx}><InputLabel>Stage</InputLabel><Select value={stageFilter} label="Stage" onChange={(e) => setStageFilter(e.target.value)}><MenuItem value="all">All stages</MenuItem>{stages.map((stage) => <MenuItem key={stage} value={stage}>{stage}</MenuItem>)}</Select></FormControl>
        </Box>

        <TableContainer sx={tableContainerSx}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell padding="checkbox"><Checkbox size="small" checked={filtered.length > 0 && filtered.every((row) => selected.includes(row.id))} indeterminate={selected.length > 0 && !filtered.every((row) => selected.includes(row.id))} onChange={(e) => setSelected(e.target.checked ? filtered.map((row) => row.id) : [])} /></TableCell>
              <TableCell>Account</TableCell><TableCell>Stage</TableCell><TableCell>Progress</TableCell><TableCell>Verification</TableCell><TableCell>Payment</TableCell><TableCell>Last activity</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {filtered.map((row) => {
                const stage = humanRegistrationStage(row);
                const tone = toneForStage(stage);
                const progress = registrationProgress(row);
                return <TableRow key={row.id} hover>
                  <TableCell padding="checkbox"><Checkbox size="small" checked={selected.includes(row.id)} onChange={(e) => setSelected((prev) => e.target.checked ? [...new Set([...prev,row.id])] : prev.filter((id) => id !== row.id))} /></TableCell>
                  <TableCell><Typography sx={{ fontWeight: 850, color: workspacePalette.ink, fontSize: '.73rem' }}>{row.displayName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || 'Unnamed account'}</Typography><Typography sx={{ color: workspacePalette.inkLight, fontSize: '.62rem' }}>{row.email || row.phone || row.phoneNumber || row.id}</Typography>{row.companyName && <Typography sx={{ color: workspacePalette.inkFaint, fontSize: '.58rem', mt: .2 }}>{row.companyName}</Typography>}</TableCell>
                  <TableCell><StatusChip label={stage} color={tone} /></TableCell>
                  <TableCell sx={{ minWidth: 130 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: .7 }}><LinearProgress variant="determinate" value={progress} sx={{ flex: 1, height: 6, borderRadius: 99, bgcolor: alpha(tone,.08), '& .MuiLinearProgress-bar': { bgcolor: tone, borderRadius: 99 } }} /><Typography sx={{ fontSize: '.58rem', fontWeight: 800, color: workspacePalette.inkLight }}>{progress}%</Typography></Box></TableCell>
                  <TableCell>{verificationLabel(row) === 'Verified' ? <StatusChip label="Verified" color={workspacePalette.success} icon={<CheckCircle />} /> : <StatusChip label="Unverified" color={workspacePalette.warning} icon={<Warning />} />}</TableCell>
                  <TableCell><Chip size="small" label={String(row.paymentStatus || 'not required').replace(/_/g,' ')} sx={{ fontWeight: 800, bgcolor: alpha(workspacePalette.amber,.08), color: workspacePalette.amber }} /></TableCell>
                  <TableCell>{fmt(row.updatedAt || row.lastActivityAt || row.createdAt)}</TableCell>
                  <TableCell align="right"><Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: .3 }}><Tooltip title="View registration"><Button size="small" onClick={() => openDetail(row)} sx={{ minWidth: 32, width: 32, p: 0 }}><Visibility fontSize="small" /></Button></Tooltip><Tooltip title="Send reminder"><Button size="small" disabled={actionBusy} onClick={() => sendReminder(row)} sx={{ minWidth: 32, width: 32, p: 0 }}><Email fontSize="small" /></Button></Tooltip></Box></TableCell>
                </TableRow>;
              })}
              {!filtered.length && <TableRow><TableCell colSpan={8}><Box sx={{ py: 5, textAlign: 'center' }}><CheckCircle sx={{ color: workspacePalette.success, fontSize: 34 }} /><Typography sx={{ mt: .8, fontWeight: 850, color: workspacePalette.ink }}>No registrations match these filters</Typography><Typography sx={{ color: workspacePalette.inkLight, fontSize: '.7rem' }}>Incomplete accounts will appear here automatically.</Typography></Box></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </WorkspacePanel>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        {detail && <>
          <DialogTitle>Registration · {detail.displayName || detail.email || detail.id}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 1, mb: 1.2 }}>
              {[['Stage',humanRegistrationStage(detail)],['Verification',verificationLabel(detail)],['Payment',String(detail.paymentStatus || 'not_required').replace(/_/g,' ')],['Started',fmt(detail.registrationStartedAt || detail.createdAt)],['Last activity',fmt(detail.updatedAt || detail.lastActivityAt)],['Reminders',Number(detail.registrationReminderCount || 0)]].map(([label,value]) => <Box key={label} sx={{ p: 1, borderRadius: '12px', bgcolor: '#F7FAFC', border: `1px solid ${workspacePalette.hairline}` }}><Typography sx={{ fontSize: '.55rem', fontWeight: 900, textTransform: 'uppercase', color: workspacePalette.inkFaint }}>{label}</Typography><Typography sx={{ mt: .25, fontSize: '.72rem', fontWeight: 800, color: workspacePalette.ink, wordBreak: 'break-word' }}>{value}</Typography></Box>)}
            </Box>
            <PanelTitle title="Reminder History" subtitle="Most recent 50 attempts" icon={<Schedule />} accent={workspacePalette.teal} />
            <Box sx={{ display: 'grid', gap: .6 }}>
              {history.length ? history.map((item) => <Box key={item.id} sx={{ p: .9, borderRadius: '11px', border: `1px solid ${workspacePalette.hairline}`, bgcolor: '#fff' }}><Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}><Typography sx={{ fontSize: '.68rem', fontWeight: 800, color: workspacePalette.ink }}>{(item.channels || []).join(' · ') || 'Notification'}</Typography><Typography sx={{ fontSize: '.58rem', color: workspacePalette.inkLight }}>{fmt(item.createdAt)}</Typography></Box><Typography sx={{ fontSize: '.62rem', color: workspacePalette.inkSoft, mt: .25 }}>{item.message || 'Registration reminder'}</Typography></Box>) : <Typography sx={{ color: workspacePalette.inkLight, fontSize: '.68rem' }}>No reminder history yet.</Typography>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap' }}>
              <Button disabled={actionBusy} startIcon={<Email />} onClick={() => sendReminder(detail,['in_app','email'])} sx={quietButtonSx}>Email</Button>
              <Button disabled={actionBusy} startIcon={<WhatsApp />} onClick={() => sendReminder(detail,['in_app','whatsapp'])} sx={quietButtonSx}>WhatsApp</Button>
              <Button disabled={actionBusy} startIcon={<Sms />} onClick={() => sendReminder(detail,['in_app','sms'])} sx={quietButtonSx}>SMS</Button>
              <Button onClick={() => copyResume(detail)} sx={quietButtonSx}>Copy resume link</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap' }}>
              <Button disabled={actionBusy} onClick={() => run(() => resetRegistrationReminders(detail.id),'Reminder counter reset.')} sx={quietButtonSx}>Reset reminders</Button>
              <Button disabled={actionBusy} onClick={() => run(() => extendRegistration(detail.id,7),'Registration extended by 7 days.')} sx={quietButtonSx}>Extend 7 days</Button>
              {String(detail.registrationStatus || '').toLowerCase() === 'expired'
                ? <Button disabled={actionBusy} startIcon={<CheckCircle />} onClick={() => run(() => reactivateRegistration(detail.id),'Registration reactivated.')} color="success">Reactivate</Button>
                : <Button disabled={actionBusy} startIcon={<Cancel />} onClick={() => run(() => expireRegistration(detail.id),'Registration expired.')} color="error">Expire</Button>}
            </Box>
          </DialogActions>
        </>}
      </Dialog>
    </WorkspacePage>
  );
}
